import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { eslTourStats } from '../api/tournaments';
import styles from '../static/styles/TournamentDetails.module.css';

const TournamentDetails = () => {
  const { tournamentId } = useParams();
  const [tournamentData, setTournamentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTournamentData = async () => {
      if (!tournamentId) {
        setError('Tournament ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await eslTourStats(tournamentId);
        
        if (response && response.rounds) {
          //console.log(response.rounds)
          setTournamentData(response);
        } else {
          setError('No tournament data available');
        }
      } catch (err) {
        //console.error('Error fetching tournament stats:', err);
        setError('Failed to load tournament data');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentId]);

  const getResultTypeColor = (resultType) => {
    switch (resultType) {
      case 'normal': return '#4CAF50';
      case 'walkover': return '#FF9800';
      case 'both_absent': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  // Sort matches by priority (winners first, then actual games, then no-shows)
  const sortMatchesByPriority = (matches) => {
    return [...matches]
      .filter(match => match.result.result_type !== 'both_absent')
      .sort((a, b) => {
        const aHasWinner = a.result.winner ? 1 : 0;
        const bHasWinner = b.result.winner ? 1 : 0;
        const aHasPlayers = a.players.some(p => p.has_appeared) ? 1 : 0;
        const bHasPlayers = b.players.some(p => p.has_appeared) ? 1 : 0;

        // Priority: has winner > has players > no shows
        return (bHasWinner - aHasWinner) || (bHasPlayers - aHasPlayers);
      });
  };

  // Organize tournament data for binary tree display (bottom to top)
  const organizeBinaryTree = (rounds) => {
    if (!rounds || rounds.length === 0) return { treeRounds: [], winner: null };
    
    // Sort rounds by round number (ascending) and organize matches by priority
    const sortedRounds = [...rounds]
      .sort((a, b) => a.round_number - b.round_number)
      .map(round => ({
        ...round,
        matches: sortMatchesByPriority(round.matches)
      }));
    
    // Find winner from last round - either single match final or only one winner remaining
    let winner = null;
    if (sortedRounds.length > 0) {
      const lastRound = sortedRounds[sortedRounds.length - 1];
      
      // Check if any match is still upcoming
      const hasUpcomingMatches = lastRound.matches.some(match => match.status === "upcoming");
      
      if (!hasUpcomingMatches) {
        if (lastRound.matches.length === 1) {
          // Traditional final with single match
          const finalMatch = lastRound.matches[0];
          winner = finalMatch.result.winner || null;
        } else {
          // Check if only one winner exists across all matches in the round
          const winners = lastRound.matches
            .map(match => match.result.winner)
            .filter(w => w); // Remove null/undefined winners
          
          if (winners.length === 1) {
            winner = winners[0];
          }
        }
      }
    }
    
    // Reverse the rounds so latest appears at top, earliest at bottom
    const treeRounds = [...sortedRounds].reverse();
    
    return {
      treeRounds,
      winner
    };
  };

  const treeData = tournamentData?.rounds ? organizeBinaryTree(tournamentData.rounds) : { treeRounds: [], winner: null };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading tournament details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!tournamentData || !tournamentData.rounds) {
    return (
      <div className={styles.container}>
        <div className={styles.noData}>No tournament data available</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Tournament Results</h1>
      </div>

      <div className={styles.treeContainer}>
        {/* Binary tree structure - rounds from top to bottom */}
        <div className={styles.binaryTree}>
          {/* Winner at the top (root of tree) - only if there's an actual winner */}
          {treeData.winner && (
            <div className={styles.winnerRoot}>
              <div className={styles.trophy}>🏆</div>
              <div className={styles.championTitle}>CHAMPION</div>
              <div className={styles.championName}>{treeData.winner}</div>
            </div>
          )}
          
          {treeData.treeRounds.map((round, roundIndex) => {
            // Calculate the visual level (0 = top/latest round)
            const level = roundIndex;
            // Calculate spacing based on level (more matches = more spread)
            const matchSpacing = Math.max(200, round.matches.length * 40);
            
            return (
              <div 
                key={round.round_number} 
                className={styles.treeLevel}
                style={{
                  '--level': level,
                  '--match-spacing': `${matchSpacing}px`,
                }}
              >
                <div className={styles.levelHeader}>
                  <h3>{round.title}</h3>
                </div>
                
                <div className={styles.levelMatches}>
                  {round.matches.map((match, matchIndex) => (
                    <div 
                      key={match.room_id} 
                      className={styles.treeMatch}
                      style={{
                        '--match-index': matchIndex,
                      }}
                    >
                      <div className={styles.matchNode}>
                        <div className={styles.teams}>
                          {match.players.map((player, playerIndex) => (
                            <div 
                              key={`${match.room_id}-${playerIndex}`}
                              className={`${styles.team} ${
                                match.result.winner === player.username ? styles.winner : 
                                match.result.loser === player.username ? styles.loser : ''
                              }`}
                            >
                              <span className={styles.teamName}>
                                {player.username.length > 10 ? 
                                  `${player.username.substring(0, 10)}...` : 
                                  player.username}
                              </span>
                              {match.result.winner === player.username && (
                                <span className={styles.winnerIcon}>👑</span>
                              )}
                            </div>
                          ))}
                          
                          {match.players.length === 1 && (
                            <div className={`${styles.team} ${styles.bye}`}>
                              <span className={styles.teamName}>BYE</span>
                            </div>
                          )}
                        </div>
                        
                        <div className={styles.matchMeta}>
                          <span className={styles.roomId}>#{match.room_id}</span>
                          <span 
                            className={styles.resultBadge}
                            style={{ backgroundColor: getResultTypeColor(match.result.result_type) }}
                          >
                            {match.result.result_type === 'normal' ? '✓' : 
                             match.result.result_type === 'walkover' ? 'W' : '✗'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Tree branches connecting to next level up */}
                      {roundIndex > 0 && match.result.winner && (
                        <div className={styles.treeBranch}></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TournamentDetails;